/**
 * SafeGirl Cart Manager - v2
 *
 * Centralized cart mutation queue. ALL cart writes (gifts, rubber-band
 * sync, bundle operations) go through CartManager._flush().
 *
 * Key guarantees:
 *  - Sequential execution: no concurrent /cart/*.js calls
 *  - Single Section Rendering call per flush cycle
 *  - UI locked (pointer-events + aria-busy) throughout
 *  - AbortController with 8s timeout on every fetch
 *  - _ownRequest flag prevents re-entrant scheduling from fetch hooks
 *  - No MutationObserver (removed - caused feedback loops)
 */
(function () {
  "use strict";

  var AUTO_RUBBER_PROP     = "_sg_auto_rubber_gift";
  var AUTO_BOOK_GIFT_PROP  = "_sg_bundle_gift";
  var AUTO_MILLUT_GIFT_PROP = "_sg_auto_millut_gift";
  var AUTO_SIGNUP_GIFT_PROP = "_sg_signup_gift";
  var BYPASS_POPUP_PROP    = "_bypass_popup";

  // Cart-drawer upsell (27.09.2026): lines added from the drawer's upsell strip
  // carry _sg_drawer_upsell=1 and get an extra 30 % from the Discount Function,
  // but only on the first DRAWER_UPSELL_MAX_UNITS units per cart (run.js,
  // DRAWER_UPSELL_MAX_UNITS - keep both in lockstep). Since 31.10.2026 (owner)
  // tagged units are not capped: whole tagged lines that fit the budget get
  // the 30 %, and every other unit is priced by its product tiers. A tagged
  // line is kept at 1 unit; extra units go to a DRAWER_EXTRA_PROP line.
  var DRAWER_UPSELL_PROP      = "_sg_drawer_upsell";
  var DRAWER_UPSELL_MAX_UNITS = 2;
  var DRAWER_EXTRA_PROP       = "_sg_drawer_extra";

  var BUNDLE_PRODUCT_IDS = [
    8491023761591,
    8473135087799,
    8491024220343,
    8491023827127,
    8491024023735,
  ];

  var BOOK_VARIANT_ID    = 47170737471671;
  var BOOK_PRODUCT_ID    = 8502297919671;
  var ALARM_PRODUCT_ID   = 8473135087799;
  var RUBBER_BAND_VARIANT_ID = 47118423326903;
  var ALARM_QTY_THRESHOLD = 2;

  // SafeDoor → ערכת מילוט (black) gift: when the cart has 2+ standalone
  // SafeDoor units, auto-add 1 ערכת מילוט (black variant) which the
  // Discount Function will mark 100% off. Fully separate from the
  // alarm→rubber and full-kit→book gift flows.
  var SAFEDOOR_PRODUCT_ID = 8561745920183;
  var MILLUT_GIFT_PRODUCT_ID = 8491024023735;
  var MILLUT_GIFT_VARIANT_ID = 47316503494839;
  var SAFEDOOR_GIFT_THRESHOLD = 2;

  // Newsletter signup → SafeKey (pink) gift: when the visitor submits the
  // Flashy newsletter popup, a localStorage flag (sg_signup_gift) is set. While
  // that flag is set AND the cart has >= 1 paid item, auto-add 1 SafeKey (pink),
  // which the Discount Function marks 100% off via the _sg_signup_gift tag. The
  // paid-item gate is MANDATORY: a lone 100%-off gift zeroes the cart total and
  // makes Shopify bypass the payment gateway (free-checkout mode). Fully separate
  // from the alarm->rubber, SafeDoor->millut, and full-kit->book gift flows.
  var SAFEKEY_PRODUCT_ID      = 8491023761591;
  var SAFEKEY_GIFT_VARIANT_ID = 47118425325751; // SafeKey - ורוד
  var SIGNUP_GIFT_FLAG_KEY    = "sg_signup_gift";
  // No expiry, per explicit merchant decision (07/2026): one signup entitles
  // this browser to a free SafeKey on every order, indefinitely. Repeat
  // purchases from the same browser each get the gift - that is intended.

  var FETCH_TIMEOUT_MS = 8000;
  var DEBOUNCE_MS      = 350;

  // ─── CartManager ──────────────────────────────────────────────────────────

  var PRELOAD_MAX_AGE_MS = 1500;

  var CartManager = {
    _queue: [],
    _processing: false,
    _ownRequest: false,
    _debounceTimer: null,
    _firstRun: true,
    _lastActionKind: null,  // 'change' | 'add' | 'remove' | 'clear' | null
    _mutationSince: 0,       // bumped on every cart-mutating fetch so preload can self-invalidate
    _activeFlush: null,      // promise of the in-flight (or most recent) _flush - lets flushNow() chain
    _inOwnCall: 0,           // >0 only while one of OUR OWN fetches is in flight (see _fetchWithTimeout)
    _pendingSync: false,     // a foreign mutation raced an active flush - re-sync when it ends

    enqueue: function (task) {
      this._queue.push(task);
      /* SG-FIX: record the promise (flushNow() chains on _activeFlush; an
       * unrecorded flush made flushNow resolve instantly against the
       * _processing guard, breaking its "gifts applied" contract). When a
       * flush IS running, the task stays queued and the finally-block
       * re-flush below drains it immediately - previously it sat dormant
       * until some unrelated future mutation (e.g. a cart-reset confirmed
       * mid-flush silently wiping the cart minutes later). */
      if (!this._processing) this._activeFlush = this._flush();
    },

    // Public: clear the entire cart, then trigger the standard render pipeline.
    clearCart: function () {
      var self = this;
      this.enqueue(function () {
        return self._postJson("/cart/clear.js", {});
      });
    },

    scheduleSync: function (source) {
      /* SG-FIX: never silently drop a sync. The old `if (this._ownRequest)
       * return;` discarded any foreign mutation whose response landed while
       * a flush was running (~0.6-1.5s after every cart action) - the gift
       * plan was then never recomputed for it: an auto-gift stayed in the
       * cart charged at full price, or a newly-earned gift never got added.
       * Own requests are identified per-call (_inOwnCall) and skipped as
       * before; a foreign mutation racing a flush sets _pendingSync so the
       * flush's finally-block re-syncs. */
      if (this._inOwnCall > 0) return;
      if (this._ownRequest) { this._pendingSync = true; return; }
      var self = this;
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(function () {
        if (!self._processing) {
          self._activeFlush = self._flush();
        } else {
          self._pendingSync = true;
        }
      }, DEBOUNCE_MS);
    },

    // Public: run a sync immediately (bypass the debounce) and resolve once the
    // gift plan is applied + the drawer re-rendered. The quantity-breaks ATC
    // awaits this BEFORE opening the drawer, so auto-gifts (SafeDoor → ערכת מילוט,
    // 2+ alarms → rubber band) are already in the cart when it paints. Without it
    // the drawer renders the pre-gift cart and the gift is missing until a manual
    // refresh - most visible when adding 2-3 units at once.
    flushNow: function () {
      var self = this;
      clearTimeout(this._debounceTimer);
      var prior = this._activeFlush || Promise.resolve();
      this._activeFlush = prior.then(function () { return self._flush(); });
      return this._activeFlush;
    },

    _flush: async function () {
      if (this._processing) return;
      this._processing = true;
      this._ownRequest = true;
      /* 30.09.2026 (מהירות): במסלול ההוספה (variant:add) ה-base theme כבר צייר את
       * המגירה מיד מהתשובה של /cart/add.js. ברוב ההוספות תוכנית המתנות לא משנה
       * כלום, ואז הרינדור השני כאן (עוד /cart.js + section render, ~400ms) רק חזר
       * על אותו ציור עם עמעום באמצע ("קופץ ולאגי"). לכן במסלול הזה לא נועלים
       * מראש: נועלים רק כשבאמת יש מה לשנות או לרנדר. שאר המסלולים (שינוי כמות,
       * הסרה, טעינה) לא השתנו - שם CartManager הוא המצייר היחיד. */
      var sgAddPath = this._lastActionKind === "add";
      var sgLocked = false;
      if (!sgAddPath) { this.lockUI(); sgLocked = true; }

      try {
        while (this._queue.length > 0) {
          var task = this._queue.shift();
          await task();
        }

        var cart = await this._fetchCart();
        var plan = this._computeGiftPlan(cart);
        var planMutated =
          (plan.adds && plan.adds.length > 0) ||
          (plan.removals && plan.removals.length > 0) ||
          (plan.updates && Object.keys(plan.updates).length > 0);
        if (planMutated && !sgLocked) { this.lockUI(); sgLocked = true; }

        if (plan.updates && Object.keys(plan.updates).length > 0) {
          await this._postJson("/cart/update.js", { updates: plan.updates });
        }

        var giftBookAdded = false;
        var rubberGiftAdded = false;
        var millutGiftAdded = false;
        var signupGiftAdded = false;

        if (plan.adds && plan.adds.length > 0) {
          try {
            await this._postJson("/cart/add.js", { items: plan.adds });
            giftBookAdded = plan.adds.some(function (it) { return it.id === BOOK_VARIANT_ID; });
            rubberGiftAdded = plan.adds.some(function (it) { return it.id === RUBBER_BAND_VARIANT_ID; });
            millutGiftAdded = plan.adds.some(function (it) { return it.id === MILLUT_GIFT_VARIANT_ID; });
            signupGiftAdded = plan.adds.some(function (it) {
              return it.id === SAFEKEY_GIFT_VARIANT_ID &&
                it.properties && it.properties[AUTO_SIGNUP_GIFT_PROP] === "1";
            });
          } catch (addErr) {
            console.warn("[CartManager] Gift add failed (inventory?), continuing:", addErr.message);
          }
        }

        if (plan.removals && plan.removals.length > 0) {
          for (var i = 0; i < plan.removals.length; i++) {
            try {
              await this._postJson("/cart/change.js", { id: plan.removals[i], quantity: 0 });
            } catch (rmErr) {
              console.warn("[CartManager] Removal failed, continuing:", rmErr.message);
            }
          }
        }

        // Our own gift mutations bypass the fetch-hook (_ownRequest), so nothing
        // bumps _mutationSince or kills the drawer-open preload. A section
        // snapshot captured on open BEFORE the gift add then passes the
        // "preloadMutationAt >= _mutationSince" check in _renderSection and
        // paints the drawer WITHOUT the just-added gift (it still shows at
        // checkout / after reopen - the exact "gift invisible until refresh"
        // bug). Stamp the mutation + drop the preload so _renderSection always
        // re-fetches after we actually changed the cart.
        if (planMutated) {
          this._mutationSince = Date.now();
          window.__sgCartSectionPromise = null;
          window.__sgCartSectionAt = 0;
          window.__sgCartSectionMutationStamp = 0;
        }

        /* 30.09.2026: המגירה כבר מצוירת נכון כשה-base theme צייר את אותו סל בדיוק
         * (חתימת פריטים+מחירים מ-cart:change של variant:add, ראו safely-overrides.js)
         * ותוכנית המתנות לא שינתה כלום: מדלגים על הרינדור הכפול ומודיעים לכולם
         * שהמגירה מסונכרנת (אותו אירוע, בלי דגלי מתנה). */
        var sgBaseSig = window.__sgBaseRepaintSig || "";
        var sgSkipRender = !planMutated && sgAddPath && !this._firstRun &&
          !!sgBaseSig && sgBaseSig === this.cartSig(cart);
        if (sgSkipRender) {
          document.dispatchEvent(new CustomEvent("sg:cart-section-rendered", {
            detail: { giftBookAdded: false, rubberGiftAdded: false, millutGiftAdded: false, signupGiftAdded: false, skipped: true },
          }));
        } else {
          if (!sgLocked) { this.lockUI(); sgLocked = true; }
          await this._renderSection(giftBookAdded, rubberGiftAdded, millutGiftAdded, signupGiftAdded);
        }
        /* SG-FIX: our gift mutations bypass the fetch hooks (_inOwnCall), so
         * no cart:change ever fired for them - the /cart PAGE (which
         * re-renders .cart-order only on cart:change) kept showing the
         * pre-mutation cart: a "charged ₪39.90" phantom gift line after it
         * was auto-removed, or a missing gift after it was auto-added.
         * _renderSection only repaints the drawer. On the cart page,
         * dispatch a synthetic cart:change with the fresh cart so the
         * existing page renderer catches up. */
        if (planMutated &&
            window.themeVariables && window.themeVariables.settings &&
            window.themeVariables.settings.pageType === "cart") {
          try {
            var sgFreshCart = await this._fetchCart();
            document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
              bubbles: true,
              detail: { baseEvent: "sg:gift-sync", cart: sgFreshCart },
            }));
          } catch (eRc) { /* page stays stale until next user action - old behavior */ }
        }
        this._firstRun = false;
      } catch (err) {
        console.error("[CartManager]", err);
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      } finally {
        this._lastActionKind = null;
        this._ownRequest = false;
        this._processing = false;
        this.unlockUI();
        /* SG-FIX: drain work that raced this flush - a foreign mutation that
         * set _pendingSync, or a task enqueued after the while-loop exited
         * (clearCart mid-flush). Without this, both were silently deferred
         * to some unrelated future mutation. */
        if (this._pendingSync || this._queue.length > 0) {
          this._pendingSync = false;
          var selfNext = this;
          this._activeFlush = Promise.resolve().then(function () { return selfNext._flush(); });
        }
      }
    },

    // ── Fetch helpers (AbortController + timeout) ──────────────────────────

    _fetchWithTimeout: function (url, options) {
      // Keep the active storefront language for cart reads, updates and gifts.
      if (typeof url === "string" && /^\/cart(?:[/.?]|$)/.test(url)) {
        var localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        url = localeRoot.replace(/\/?$/, "/") + url.slice(1);
      }
      var ctrl = new AbortController();
      var timer = setTimeout(function () { ctrl.abort(); }, FETCH_TIMEOUT_MS);
      var opts = Object.assign({}, options || {}, {
        credentials: "same-origin",
        signal: ctrl.signal,
      });
      if (!opts.method || opts.method.toUpperCase() === "GET") opts.cache = "no-store";
      /* SG-FIX: mark OUR OWN requests per-call (not per-flush) so the fetch
       * hook can tell them apart from foreign mutations that start between
       * our awaited calls - those must be tracked, not ignored. */
      CartManager._inOwnCall++;
      return fetch(url, opts).finally(function () {
        clearTimeout(timer);
        CartManager._inOwnCall = Math.max(0, CartManager._inOwnCall - 1);
      });
    },

    _fetchCart: function () {
      return this._fetchWithTimeout("/cart.js", {
        headers: { Accept: "application/json" },
      }).then(function (r) {
        if (!r.ok) throw new Error("GET /cart.js " + r.status);
        return r.json();
      });
    },

    _postJson: function (url, body) {
      return this._fetchWithTimeout(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(body),
      }).then(function (r) {
        if (!r.ok) throw new Error("POST " + url + " " + r.status);
        return r.json();
      });
    },

    // ── Gift plan computation (pure logic, no fetch) ───────────────────────
    //
    // TWO GIFTS, TWO DIFFERENT RULES - DO NOT UNIFY:
    //
    //   1. EBOOK (BOOK_PRODUCT_ID) - strict kit-group rules:
    //      - Auto-add only when the cart contains a "full kit group", i.e. one
    //        _bundle_cart_group_id whose lines cover ALL 5 BUNDLE_PRODUCT_IDS.
    //      - Auto-remove the previously-gifted book when that condition is no
    //        longer met. Only ever removes lines tagged _sg_bundle_gift="1"
    //        so books added manually from the PDP/quick-buy are NEVER touched.
    //
    //   2. RUBBER BAND (RUBBER_BAND_VARIANT_ID) - old pre-kit-group rules:
    //      - Auto-add based purely on ungrouped alarmQty crossing a threshold.
    //      - NEVER gated on hasFullKitGroup. Rationale: unlike the one-per-cart
    //        EBOOK, customers can legitimately have a manually-added rubber
    //        plus the auto-gift. Auto-remove targets only lines tagged
    //        _sg_auto_rubber_gift="1" so manual adds are NEVER touched.
    //
    // Keeping these two flows separate is intentional. Do not refactor them
    // into a shared code path without re-deriving both contracts first.
    _computeGiftPlan: function (cart) {
      var plan = { updates: {}, adds: [], removals: [] };
      if (!cart || !cart.items) return plan;

      var items = cart.items;
      var alarmQty = 0;
      var anyBookLine = null;
      var giftBookLines = [];
      var rubberGiftLines = [];
      var rubberAnyLines = [];
      // SafeDoor → ערכת מילוט gift counters (separate from alarm→rubber path)
      var safedoorQty = 0;
      var millutGiftLines = [];
      var millutAnyLines = [];
      // SafeKey newsletter-signup gift accounting
      var safekeyGiftLines = [];
      var paidItemCount = 0;
      // groupMap[groupId] = Set-like object of product_ids inside that bundle group.
      // Book gift is tied to the presence of a real kit group (ALL 5 bundle pids
      // in the same _bundle_cart_group_id), NOT to 5 unrelated standalone items.
      var groupMap = {};
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var pid = item.product_id;

        // Count genuinely paid lines (anything not one of our auto-gifts). Gates
        // the signup gift so a lone 100%-off SafeKey can't zero the cart total.
        var isAnyGiftLine =
          this._propIs(item, AUTO_RUBBER_PROP, "1") ||
          this._propIs(item, AUTO_BOOK_GIFT_PROP, "1") ||
          this._propIs(item, AUTO_MILLUT_GIFT_PROP, "1") ||
          this._propIs(item, AUTO_SIGNUP_GIFT_PROP, "1");
        if (!isAnyGiftLine) paidItemCount += item.quantity;

        var gid = item.properties && item.properties._bundle_cart_group_id;
        if (gid) {
          if (!groupMap[gid]) groupMap[gid] = {};
          groupMap[gid][pid] = true;
        }

        if (pid === ALARM_PRODUCT_ID) {
          var inBundle = item.properties && (item.properties._bundle_cart_group_id || item.properties._bundle_parent_product_id);
          if (!inBundle) alarmQty += item.quantity;
        }
        if (pid === BOOK_PRODUCT_ID) {
          anyBookLine = item;
          if (this._propIs(item, AUTO_BOOK_GIFT_PROP, "1")) {
            giftBookLines.push(item);
          }
        }
        if (item.variant_id === RUBBER_BAND_VARIANT_ID) {
          // IMPORTANT: the "pink scrunchie" variant 47118423326903 is ALSO the
          // default variant shoppers can pick inside a Full Kit / mini-bundle
          // component slot. If we treated those bundle-line scrunchies as
          // "existing rubber bands", the gift-add branch below would never fire
          // when someone has kits in cart and then buys 2 standalone alarms -
          // even though the gift rubber band is conceptually separate from the
          // scrunchie slot inside a bundle.
          //
          // Per product decision (user, 2026-04): the rubber-band gift must be
          // granted whenever 2+ standalone alarms enter the cart, regardless of
          // what's inside any bundle. So we only count STANDALONE rubber-band
          // lines here. Auto-add/auto-remove behavior is otherwise unchanged.
          var rubInBundle = item.properties && (
            item.properties._bundle_cart_group_id ||
            item.properties._bundle_parent_product_id
          );
          if (!rubInBundle) {
            rubberAnyLines.push(item);
            if (this._propIs(item, AUTO_RUBBER_PROP, "1")) {
              rubberGiftLines.push(item);
            }
          }
        }

        // SafeDoor → ערכת מילוט gift accounting.
        // Same in-bundle exclusion logic as alarms/rubber: a SafeDoor that's
        // part of a kit slot (carries _bundle_cart_group_id) does NOT count
        // toward the standalone gift trigger, and a millut sitting inside a
        // kit's millut slot is NOT eligible for the auto-100%-off discount.
        // Match by product_id OR handle so the gift still triggers if a theme
        // import shifted product_ids (handle is stable across stores).
        var isSafedoor = pid === SAFEDOOR_PRODUCT_ID || item.handle === 'safedoor';
        if (isSafedoor) {
          var sdInBundle = item.properties && (
            item.properties._bundle_cart_group_id ||
            item.properties._bundle_parent_product_id
          );
          if (!sdInBundle) safedoorQty += item.quantity;
        }
        var isMillut = pid === MILLUT_GIFT_PRODUCT_ID || item.handle === 'safetyhammer';
        if (isMillut) {
          var millutInBundle = item.properties && (
            item.properties._bundle_cart_group_id ||
            item.properties._bundle_parent_product_id
          );
          if (!millutInBundle) {
            millutAnyLines.push(item);
            if (this._propIs(item, AUTO_MILLUT_GIFT_PROP, "1")) {
              millutGiftLines.push(item);
            }
          }
        }

        // SafeKey newsletter-signup gift: collect STANDALONE SafeKey lines tagged
        // _sg_signup_gift so we never touch a SafeKey bought manually or one
        // sitting inside a kit slot. Mirrors the rubber/millut pattern.
        var isSafekey = pid === SAFEKEY_PRODUCT_ID || item.handle === "safekey";
        if (isSafekey) {
          var skInBundle = item.properties && (
            item.properties._bundle_cart_group_id ||
            item.properties._bundle_parent_product_id
          );
          if (!skInBundle && this._propIs(item, AUTO_SIGNUP_GIFT_PROP, "1")) {
            safekeyGiftLines.push(item);
          }
        }
      }

      // A "full kit group" = one cart group whose lines cover every bundle pid.
      // Going from 3 kits to 2 still has a full kit group; going 1→0 loses it.
      var hasFullKitGroup = false;
      for (var gk in groupMap) {
        if (!Object.prototype.hasOwnProperty.call(groupMap, gk)) continue;
        var allIn = true;
        for (var bi2 = 0; bi2 < BUNDLE_PRODUCT_IDS.length; bi2++) {
          if (!groupMap[gk][BUNDLE_PRODUCT_IDS[bi2]]) { allIn = false; break; }
        }
        if (allIn) { hasFullKitGroup = true; break; }
      }

      // ── Book gift: add if a full kit GROUP exists & no book exists ──
      // SAFETY INVARIANT: we only ever enqueue removals for book lines that
      // carry _sg_bundle_gift="1" (giftBookLines). Books added manually from
      // the storefront PDP/quick-buy do not have this property and are never
      // touched here.
      if (hasFullKitGroup && !anyBookLine) {
        var bookProps = {};
        bookProps[AUTO_BOOK_GIFT_PROP] = "1";
        bookProps[BYPASS_POPUP_PROP] = "true";
        plan.adds.push({ id: BOOK_VARIANT_ID, quantity: 1, properties: bookProps });
      } else if (!hasFullKitGroup && giftBookLines.length > 0) {
        for (var bi = 0; bi < giftBookLines.length; bi++) {
          var gl = giftBookLines[bi];
          if (!this._propIs(gl, AUTO_BOOK_GIFT_PROP, "1")) {
            console.warn("[CartManager] Refusing to remove non-gift book line", gl && gl.key);
            continue;
          }
          plan.removals.push(gl.key);
        }
      }

      // ── Cart-drawer upsell: one unit per tagged line (31.10.2026, owner) ──
      // Tagged units are not capped. The discount function gives the extra
      // 30 % to WHOLE tagged lines that fit the budget, so "+" (or a typed
      // quantity) on a tagged line could make it 2 units and push another
      // add-on out of the offer. The offer stays with the first unit of each
      // add-on the customer switched on: a tagged line is kept at 1 unit and
      // the rest moves to a sibling line marked DRAWER_EXTRA_PROP. The
      // function does not read that property, so those units are priced by
      // their product tiers, and the strip's switch-off removes them too.
      for (var ui = 0; ui < items.length; ui++) {
        var uit = items[ui];
        if (uit.quantity <= 1 || !this._propIs(uit, DRAWER_UPSELL_PROP, "1")) continue;
        if (uit.properties && (uit.properties._bundle_cart_group_id || uit.properties._bundle_parent_product_id)) continue;
        if (this._propIs(uit, AUTO_RUBBER_PROP, "1") || this._propIs(uit, AUTO_BOOK_GIFT_PROP, "1") ||
            this._propIs(uit, AUTO_MILLUT_GIFT_PROP, "1") || this._propIs(uit, AUTO_SIGNUP_GIFT_PROP, "1")) continue;
        var extraProps = {};
        for (var upk in uit.properties) {
          if (Object.prototype.hasOwnProperty.call(uit.properties, upk) && upk !== DRAWER_UPSELL_PROP) extraProps[upk] = uit.properties[upk];
        }
        extraProps[DRAWER_EXTRA_PROP] = "1";
        plan.updates[uit.key] = 1;
        plan.adds.push({ id: uit.variant_id, quantity: uit.quantity - 1, properties: extraProps });
      }

      // Cap ebook to max 1 unit
      for (var ei = 0; ei < items.length; ei++) {
        if (items[ei].product_id === BOOK_PRODUCT_ID && items[ei].quantity > 1) {
          plan.updates[items[ei].key] = 1;
        }
      }

      // ── Rubber band gift ──
      // Spec: exactly 1 free rubber per cart when standalone alarms >= 2.
      // Singular regardless of alarm count, matching run.js. We only add
      // an auto-gift rubber when no rubber line exists at all (manual or
      // gifted) - otherwise the existing rubber is the one that gets the
      // 100% discount applied by the discount function.
      var rubberGiftEligible = alarmQty >= ALARM_QTY_THRESHOLD;
      var hasAnyRubber = rubberAnyLines.length > 0;

      if (rubberGiftEligible && !hasAnyRubber) {
        var rbProps = {};
        rbProps[AUTO_RUBBER_PROP] = "1";
        plan.adds.push({ id: RUBBER_BAND_VARIANT_ID, quantity: 1, properties: rbProps });
      } else if (!rubberGiftEligible && rubberGiftLines.length > 0) {
        for (var ri = 0; ri < rubberGiftLines.length; ri++) {
          plan.removals.push(rubberGiftLines[ri].key);
        }
      }

      // ── SafeDoor → ערכת מילוט gift ──
      // Spec: exactly 1 free ערכת מילוט (BLACK variant) per cart when the
      // standalone SafeDoor qty is ≥ 2. Capped at 1 even if customer adds 3+
      // SafeDoor - no compounding gifts. Auto-add only when no millut line of
      // any kind exists standalone (manual or gift) - otherwise the existing
      // line is what gets the 100% discount applied by the discount Function,
      // mirroring the rubber-band behavior.
      // Strict separation from rubber: this trigger is independent of alarm
      // qty, so a customer with 2 alarms AND 2 SafeDoor receives BOTH gifts.
      var millutGiftEligible = safedoorQty >= SAFEDOOR_GIFT_THRESHOLD;
      var hasAnyMillut = millutAnyLines.length > 0;

      if (millutGiftEligible && !hasAnyMillut) {
        var mmProps = {};
        mmProps[AUTO_MILLUT_GIFT_PROP] = "1";
        plan.adds.push({ id: MILLUT_GIFT_VARIANT_ID, quantity: 1, properties: mmProps });
      } else if (!millutGiftEligible && millutGiftLines.length > 0) {
        // Only ever remove lines we explicitly tagged as auto-gift. A customer
        // who manually purchased ערכת מילוט and then dropped to <2 SafeDoor
        // keeps their manual purchase intact.
        for (var mi = 0; mi < millutGiftLines.length; mi++) {
          plan.removals.push(millutGiftLines[mi].key);
        }
      }

      // ── SafeKey newsletter-signup gift ──
      // Trigger is a client signup flag (localStorage sg_signup_gift), NOT cart
      // contents - so the "registered first, added a product later" case is
      // handled automatically: this runs on every cart sync, so the gift lands
      // the moment a paid item enters. The paid-item gate is REQUIRED (see the
      // constant comment): a lone 100%-off gift would zero the cart and make
      // Shopify bypass the payment gateway. Auto-remove only ever targets lines
      // we tagged _sg_signup_gift, so a manual SafeKey purchase is never touched.
      var signupGiftEligible = this._signupUnlocked() && paidItemCount > 0;

      if (signupGiftEligible && safekeyGiftLines.length === 0) {
        var skProps = {};
        skProps[AUTO_SIGNUP_GIFT_PROP] = "1";
        skProps[BYPASS_POPUP_PROP] = "true";
        plan.adds.push({ id: SAFEKEY_GIFT_VARIANT_ID, quantity: 1, properties: skProps });
      } else if (signupGiftEligible) {
        // Cap the tagged gift line at 1 unit (mirrors the ebook cap). The
        // drawer stepper works on gift lines, and run.js only zeroes 1 unit -
        // without the cap a bumped line reads "🎁 במתנה" while unit #2 is
        // silently charged ₪79. Also collapses the two-tab add race.
        for (var ci = 0; ci < safekeyGiftLines.length; ci++) {
          if (safekeyGiftLines[ci].quantity > 1) {
            plan.updates[safekeyGiftLines[ci].key] = 1;
          }
        }
      } else if (safekeyGiftLines.length > 0) {
        for (var sgi = 0; sgi < safekeyGiftLines.length; sgi++) {
          plan.removals.push(safekeyGiftLines[sgi].key);
        }
      }

      return plan;
    },

    _propIs: function (item, key, val) {
      var props = item.properties;
      if (!props) return false;
      if (Array.isArray(props)) {
        for (var j = 0; j < props.length; j++) {
          if (String(props[j].name) === key) return String(props[j].value) === val;
        }
        return false;
      }
      return String(props[key]) === val;
    },

    // Reads the newsletter-signup gift flag set by the Flashy popup hook (see
    // safely-overrides.js -> SafeGirlSignupGift). Any non-empty value counts -
    // the entitlement never expires (merchant decision, see the constant block).
    // The stored value is still the signup timestamp for future analytics.
    // try/catch because localStorage throws in private-mode / blocked-cookie
    // contexts.
    _signupUnlocked: function () {
      try {
        return !!localStorage.getItem(SIGNUP_GIFT_FLAG_KEY);
      } catch (e) {
        return false;
      }
    },

    // ── Section Rendering ──────────────────────────────────────────────────

    /* חתימת מצב הסל להשוואה בין מה שה-base theme צייר (cart:change של variant:add)
       לבין מה ש-CartManager הביא: מפתח שורה, כמות ומחיר סופי אחרי הנחות, סכום
       ומספר פריטים. שוויון = המגירה כבר מציגה בדיוק את הסל הזה. */
    cartSig: function (cart) {
      if (!cart || !cart.items) return "";
      var parts = [];
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        parts.push(it.key + ":" + it.quantity + ":" + it.final_line_price);
      }
      return parts.join("|") + "#" + cart.total_price + "#" + cart.item_count;
    },

    _renderSection: function (giftBookAdded, rubberGiftAdded, millutGiftAdded, signupGiftAdded) {
      var drawer = document.querySelector("cart-drawer");
      if (!drawer) {
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        return Promise.resolve();
      }

      var sec = drawer.closest(".shopify-section");
      var sid = sec && sec.id ? sec.id.replace("shopify-section-", "") : "";
      if (!sid) {
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        return Promise.resolve();
      }

      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
      var self = this;
      var revision = drawer.__sgCartRenderRevision = (drawer.__sgCartRenderRevision || 0) + 1;
      var mutationAt = self._mutationSince;

      // Consume a preloaded section response if it's fresh and pre-dates the
      // latest mutation. Otherwise fetch on-demand. This lets the drawer "open"
      // kick off the round-trip in parallel with any pending gift plan writes.
      var preload = window.__sgCartSectionPromise;
      var preloadAt = window.__sgCartSectionAt || 0;
      var preloadMutationAt = window.__sgCartSectionMutationStamp || 0;
      var preloadFresh = preload &&
        (Date.now() - preloadAt) < PRELOAD_MAX_AGE_MS &&
        preloadMutationAt >= self._mutationSince;

      var sectionReq = preloadFresh
        ? preload
        : this._fetchWithTimeout(root + "?section_id=" + encodeURIComponent(sid));

      // Always invalidate after consuming or bypassing.
      window.__sgCartSectionPromise = null;
      window.__sgCartSectionAt = 0;
      window.__sgCartSectionMutationStamp = 0;

      return sectionReq
        .then(function (r) {
          if (!r.ok) throw new Error("Section render " + r.status);
          return r.text();
        })
        .then(function (html) {
          // A later refresh or cart edit owns the current drawer now.
          if (drawer.__sgCartRenderRevision !== revision || self._mutationSince !== mutationAt) return;
          // Strip/neuter <quick-buy-drawer> tags BEFORE parsing. The parser
          // upgrades any known custom element found in the string - even in
          // a detached <div> container - which fires
          // attributeChangedCallback("id", null, X) on the new instance. That
          // callback (see DialogElement in theme.js) unconditionally binds a
          // click-delegate listener on document.body for
          // [aria-controls="X"]. Because the OLD instance (already teleported
          // to body from the user's previous open) still holds its own
          // listener, every section render stacks one more. One click on an
          // upsell then fires N show() calls → N stacked quick-buy drawers
          // blacking out the screen. We already skip recommendations
          // entirely in _swapTargeted, so neutering these tags is safe.
          html = html
            .replace(/<quick-buy-drawer\b/gi, '<div data-sg-stripped-qbd=""')
            .replace(/<\/quick-buy-drawer>/gi, "</div>");

          // 19.10.2026: parse inertly. innerHTML on a detached div still
          // upgrades every custom element inside (<cart-drawer>,
          // <cart-note-dialog>...). Each copy binds a click delegate on <body>
          // that is never removed and, on the next tap, opens a focus trap
          // around a node that is not on the page. That trap cancelled the
          // first touches inside the real drawer: the note button did not open
          // and the first swipes towards the upsells did not scroll.
          var tmp = new DOMParser().parseFromString(html, "text/html");
          var newDrawer = tmp.querySelector("#cart-drawer") || tmp.querySelector("cart-drawer");
          if (!newDrawer) throw new Error("No cart-drawer in section response");
          if (!newDrawer.querySelector(".cart-drawer__inner, .empty-state")) {
            throw new Error("Incomplete cart-drawer in section response");
          }

          self._swapTargeted(drawer, newDrawer);

          document.dispatchEvent(new CustomEvent("sg:cart-section-rendered", {
            detail: {
              giftBookAdded: !!giftBookAdded && !self._firstRun,
              rubberGiftAdded: !!rubberGiftAdded && !self._firstRun,
              millutGiftAdded: !!millutGiftAdded && !self._firstRun,
              signupGiftAdded: !!signupGiftAdded && !self._firstRun,
            },
          }));

          self._updateCartCount(tmp);
        })
        .catch(function (err) {
          console.warn("[CartManager] Section render failed, falling back", err);
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        });
    },

    _swapTargeted: function (drawer, newDrawer) {
      // Empty-cart rendering uses a completely different markup tree - just the
      // centered empty-state with a close button. None of our usual targeted
      // selectors (.cart-drawer__inner, .cart-drawer__line-items, .sg-cart-footer)
      // exist in that tree. Detect it and do a full inner swap so "reset cart"
      // actually reflects the empty cart on screen without a refresh.
      var emptyStateIncoming = !newDrawer.querySelector(".cart-drawer__inner");
      // A failed/incomplete section is not evidence that the cart is empty.
      if (emptyStateIncoming && !newDrawer.querySelector(".empty-state")) return;
      if (emptyStateIncoming) {
        try { if (typeof window.sgStopConfetti === "function") window.sgStopConfetti(); } catch (e) {}
        // Empty-state markup has no <cart-count> element (just a plain
        // <span class="count-bubble">0</span>), so _updateCartCount after
        // this swap would no-op and the header's <cart-count> would keep
        // its pre-clear number. Reset every page-level count bubble here
        // deterministically so the header flips to 0 the moment the
        // drawer flips to empty.
        document.querySelectorAll("cart-count").forEach(function (el) {
          el.textContent = "0";
          el.classList.add("opacity-0");
        });
        var kids = newDrawer.childNodes;
        var clones = [];
        for (var k = 0; k < kids.length; k++) clones.push(kids[k].cloneNode(true));
        drawer.replaceChildren.apply(drawer, clones);
        return;
      }

      // Back navigation can restore an empty DOM while Shopify still has items.
      // Targeted selectors do not exist in that tree: rebuild the children,
      // keeping the real drawer instance, focus trap and open state intact.
      if (!drawer.querySelector(".cart-drawer__inner")) {
        var restoredChildren = Array.prototype.map.call(newDrawer.childNodes, function (child) {
          return child.cloneNode(true);
        });
        drawer.replaceChildren.apply(drawer, restoredChildren);
        return;
      }

      // Recommendations subtree is static (section.settings.products) and
      // hosts <quick-buy-drawer> elements that teleport to document.body
      // when opened (Shopify Drawer.shouldAppendToBody === true). Replacing
      // it mid-open orphans the drawer + its backdrop on body, which then
      // stacks on every subsequent upsell add and blacks out the screen.
      // Skip unconditionally - no cart state depends on this list.
      var selectors = [
        ".cart-drawer__line-items",
        ".sg-cart-footer",
        '[slot="footer"]',
      ];
      for (var i = 0; i < selectors.length; i++) {
        var oldEl = drawer.querySelector(selectors[i]);
        var newEl = newDrawer.querySelector(selectors[i]);
        if (oldEl && newEl) {
          oldEl.replaceWith(newEl.cloneNode(true));
        }
      }
      // The redesigned upsell strip (27.09.2026, [data-sg-cart-upsell]) IS
      // cart-state dependent: its heading counts the remaining offer units and
      // each card's price/tag flips with the budget. It uses plain forms with
      // no <quick-buy-drawer>, so swapping it is safe. Swap only when both
      // sides are the new strip and neither carries a (stripped) quick-buy
      // drawer - the legacy list keeps the skip above.
      var oldRec = drawer.querySelector(".cart-drawer__recommendations");
      var newRec = newDrawer.querySelector(".cart-drawer__recommendations");
      if (oldRec && newRec &&
          oldRec.hasAttribute("data-sg-cart-upsell") && newRec.hasAttribute("data-sg-cart-upsell") &&
          !oldRec.querySelector("quick-buy-drawer") && !newRec.querySelector("[data-sg-stripped-qbd], quick-buy-drawer")) {
        // Anti-flicker (28.09.2026): most flushes (init, quantity steps, gift
        // sync) leave the strip identical - replacing it anyway re-created 10
        // cards, reloaded their images and reset the horizontal scroll, which
        // read as "jumpy". Compare the markup first; when the state really
        // changed, swap and carry the track's scroll position over.
        // Compare STATE, not markup: the live strip carries runtime classes and
        // attributes (is-selected from the theme carousel, is-scrollable,
        // is-picking / hidden / aria-* from the color picker) that a fresh
        // render never has, so an innerHTML comparison always differed and the
        // swap ran on every flush anyway.
        var upsellSig = function (el) {
          var head = el.querySelector(".sg-cart-upsell__titles, [data-sg-up-head]");
          var cards = el.querySelectorAll(".sg-cart-upsell__card, [data-sg-up-row]");
          var parts = [
            el.getAttribute("data-sg-upsell-left") || "",
            el.classList.contains("sg-cart-upsell--active") ? "1" : "0",
            head ? head.textContent.replace(/\s+/g, " ").trim() : "",
          ];
          for (var ci = 0; ci < cards.length; ci++) {
            var prices = cards[ci].querySelector(".sg-cart-upsell__prices, [data-sg-up-prices]");
            parts.push(
              (cards[ci].getAttribute("data-product-handle") || "") + ":" +
              (cards[ci].classList.contains("sg-cart-upsell__card--deal") || cards[ci].getAttribute("data-deal") === "1" ? "d" : "-") + ":" +
              (cards[ci].getAttribute("data-sg-state") || "") + ":" +
              (prices ? prices.textContent.replace(/\s+/g, " ").trim() : "")
            );
          }
          return parts.join("|");
        };
        if (upsellSig(oldRec) !== upsellSig(newRec)) {
          var oldTrack = oldRec.querySelector(".sg-cart-upsell__track, [data-sg-up-track]");
          var keepScroll = oldTrack ? oldTrack.scrollLeft : 0;
          var freshRec = newRec.cloneNode(true);
          oldRec.replaceWith(freshRec);
          var newTrack = freshRec.querySelector(".sg-cart-upsell__track, [data-sg-up-track]");
          if (newTrack && keepScroll) newTrack.scrollLeft = keepScroll;
        }
      }
      var oldShip = drawer.querySelector("free-shipping-bar, shipping-progress-bar, .free-shipping-bar-v2");
      var newShip = newDrawer.querySelector("free-shipping-bar, shipping-progress-bar, .free-shipping-bar-v2");
      if (oldShip && newShip) {
        oldShip.replaceWith(newShip.cloneNode(true));
      }
    },

    _updateCartCount: function (tmp) {
      var newCount = tmp.querySelector("cart-count");
      if (!newCount) return;
      var val = newCount.textContent.trim();
      document.querySelectorAll("cart-count").forEach(function (el) {
        el.textContent = val;
        el.classList.remove("opacity-0");
      });
    },

    // ── UI locking ─────────────────────────────────────────────────────────

    lockUI: function () {
      var drawer = document.querySelector("cart-drawer");
      if (drawer) {
        drawer.classList.add("cart-drawer--mutating");
        drawer.setAttribute("aria-busy", "true");
      }
    },

    unlockUI: function () {
      var drawer = document.querySelector("cart-drawer");
      if (drawer) {
        drawer.classList.remove("cart-drawer--mutating");
        drawer.setAttribute("aria-busy", "false");
      }
    },
  };

  // ─── Fetch / XHR hooks (trigger sync, skip own requests) ─────────────────

  var originalFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var result = originalFetch.apply(this, args);
    var url = typeof args[0] === "string" ? args[0] : (args[0] && args[0].url) || "";
    /* SG-FIX: skip only OUR OWN in-flight calls (_inOwnCall), not the whole
     * flush window (_ownRequest) - a foreign mutation STARTED mid-flush was
     * previously never tracked at all (no stamp, no sync). */
    if (/\/cart\/(add|change|update|clear)/.test(url) && !(CartManager._inOwnCall > 0)) {
      if (/\/cart\/change/.test(url) || /\/cart\/update/.test(url)) {
        CartManager._lastActionKind = "change";
      } else if (/\/cart\/clear/.test(url)) {
        CartManager._lastActionKind = "clear";
      } else {
        CartManager._lastActionKind = "add";
      }
      CartManager._mutationSince = Date.now();
      result.then(function () {
        CartManager.scheduleSync("fetch-hook");
      }).catch(function () {});
    }
    return result;
  };

  var originalXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    var rest = Array.prototype.slice.call(arguments, 2);
    if (typeof url === "string" && /\/cart\/(add|change|update|clear)/.test(url)) {
      if (/\/cart\/change/.test(url) || /\/cart\/update/.test(url)) {
        CartManager._lastActionKind = "change";
      } else if (/\/cart\/clear/.test(url)) {
        CartManager._lastActionKind = "clear";
      } else {
        CartManager._lastActionKind = "add";
      }
      CartManager._mutationSince = Date.now();
      this.addEventListener("load", function () {
        /* SG-FIX: scheduleSync itself now routes the mid-flush case via
         * _pendingSync - no more silent drop for XHR mutations either. */
        CartManager.scheduleSync("xhr-hook");
      });
    }
    return originalXHROpen.apply(this, [method, url].concat(rest));
  };

  // ─── Initial sync on page load ───────────────────────────────────────────

  function init() {
    CartManager.scheduleSync("init");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // ─── Exports ─────────────────────────────────────────────────────────────

  window.SafeGirlCartManager = CartManager;
  window.CartManager = CartManager;
})();
